import React from 'react'
import Reflux from 'reflux-react-16'
import _ from 'lodash'

import OrderStore from '../../stores/OrderStore.js'
import OrderActions from '../../actions/OrderActions.js'
import UserStore from '../../stores/UserStore.js'

import {Panel, Popup, P, Colors, Button, Tabs, Tab, Icon, ToolTip, S1, H6, IconButton, Popover, ListItem, Row, Stack} from '../../components/UI/index.js'
import {getSplitRadius} from '../../components/UI/buttons/index.js'
import {formSpacing, Select, RadioGroup, Check} from '../../components/UI/forms/index.js'
import OrderList from '../../components/routes/OrderList.js'
import OrderCard from '../../components/orders/OrderCard.js'
import PlanningMap from '../../components/map/PlanningMap.js'
import dbCodes from '../../../server/dbCodes.js'
import EditRouteModal from '../../components/routes/EditRouteModal.js'
import RoutePanel from '../../components/routes/RoutePanel.js'
import RouteList from '../../components/routes/RouteList.js'
import moment from 'moment'
import {formatDuration} from '../../../utils/utils.js'
import VehiclePanel from '../../components/vehicles/VehiclePanel.js'
import {withVehiclesHook} from '../../stores/VehicleStore.js'
import naturalSortBy from '../../utils/naturalSortBy.js'
import stopBundles from '../../../utils/stopBundles.js'
import t from 'src/helpers/Languages.js'
import filter from 'src/utils/filter.js'
import RouteListItem from '../../components/routes/RouteListItem.js'
import {routeBulkActions, getRouteBulkActionDisabledReason, getRouteReadyWarnings, toggleRouteBulkSelection} from '../../utils/routeBulkActions.js'
import runRouteBulkAction, {routeBulkActionOptions} from '../../components/routes/runRouteBulkAction.js'
import {getRoutePanelTotals} from '../../components/routes/routeSummary.js'
import {getETADeviation} from '@utils/routeETA.js'

// The buttons that open a closed side panel, at the map's edge halfway down
const panelButtonStyle = {position: 'absolute', top: '50%', transform: 'translateY(-50%)', height: 36, zIndex: 999}

class Planning extends Reflux.PureComponent {
    constructor(props) {
        super(props)

        this.stores = [OrderStore, UserStore]

        this.state = {
            routeSort: 'name',
            sortOrder: 'asc',
            sortedRoutes: [],
            useOptimizedPlannerRendering: props.reseller.name === 'Velomedi',
            bulkRouteAction: '',
            bulkRouteSelectedIds: {},
            bulkRouteAnchorId: '',
            bulkRouteRunning: false
        }

        this.routeOverlapCacheOptimized = {
            routes: null,
            users: null,
            data: {}
        }

        this.routeOverlapCacheLegacy = {
            routes: null,
            users: null,
            data: {}
        }

        this.onEditRoute = this.onEditRoute.bind(this)
        this.onToggleBulkRouteSelect = this.onToggleBulkRouteSelect.bind(this)
    }

    componentDidMount() {
        const {routes} = this.state
        this.setState({sortedRoutes: this.sortRoutes(routes)})
    }

    componentDidUpdate(_prevProps, prevState) {
        const {routeSort, sortOrder, routes, filteredOrders, orderQueries, activeRouteTab, orderStartDate, sortedRoutes, bulkRouteAction, bulkRouteSelectedIds, bulkRouteRunning} = this.state

        // While running, the selection mode closes itself when the run is done
        if (bulkRouteAction && !bulkRouteRunning && prevState.orderStartDate !== orderStartDate) {
            this.onCloseBulkRouteAction()
        } else if (bulkRouteAction && !bulkRouteRunning && prevState.sortedRoutes !== sortedRoutes) {
            // Routes that left the list or can't be selected anymore (e.g. made ready by a colleague) drop out of the selection
            const selectableIds = new Set(this.getBulkRouteSelectableRoutes().map((route) => route._id))
            const selectedIds = _.pickBy(bulkRouteSelectedIds, (_selected, routeId) => selectableIds.has(routeId))

            if (_.size(selectedIds) !== _.size(bulkRouteSelectedIds)) {
                this.setState({bulkRouteSelectedIds: selectedIds})
            }
        }

        if (prevState.routeSort !== routeSort || prevState.routes !== routes || prevState.sortOrder !== sortOrder || prevState.filteredOrders !== filteredOrders || prevState.orderQueries !== orderQueries || prevState.activeRouteTab !== activeRouteTab) {
            let filteredRoutes = {...routes}

            if (orderQueries.length > 0) {
                const filteredRoutesByQuery = filter.routes(filteredRoutes, orderQueries, this.props.reseller, this.props.vehicles)

                const filteredRoutesByOrders = Object.values(routes).filter((route) => {
                    return Object.values(filteredOrders).find((order) => order.routeId === route._id)
                })

                filteredRoutes = filteredRoutesByQuery

                filteredRoutesByOrders.forEach((route) => {
                    if (!filteredRoutes[route._id]) {
                        filteredRoutes[route._id] = route
                    }
                })
            }

            this.setState({
                sortedRoutes: this.sortRoutes(filteredRoutes)
            })
        }
    }

    sortRoutes(routes) {
        routes = Array.isArray(routes) ? routes : Object.values(routes)
        const {routeSort, sortOrder, activeRouteTab} = this.state
        const {vehicles, reseller} = this.props

        if (routeSort === 'name') {
            routes = naturalSortBy(routes, 'name')
        }

        if (routeSort === 'messenger') {
            routes = naturalSortBy(routes, 'messengerName')
        }

        if (routeSort === 'status') {
            const getStatus = (route) => {
                if (!route.ready && !route.started && !route.completed) { // Concept
                    return 0
                }

                if (route.ready && !route.started && !route.completed) { // Gereed
                    return 1
                }

                if (route.started && !route.completed) { // Onderweg
                    return 2
                }

                if (route.completed) { // Afgerond
                    return 3
                }
            }
            routes = routes.sort((a, b) => {
                const statusA = getStatus(a)
                const statusB = getStatus(b)

                if (statusA === 2 && statusB === 2) { // Last started route on top
                    return moment(b.startTime).valueOf() - moment(a.startTime).valueOf()
                }

                return statusA < statusB ? -1 : statusA > statusB ? 1 : 0
            })
        }

        // Started routes on top, slowest first. The sort order only flips the started routes, the other routes stay below them in a fixed order
        if (routeSort === 'progress') {
            const otherStatusOrder = (route) => {
                if (route.ready && !route.started && !route.completed) { // Gereed
                    return 0
                }

                if (!route.ready && !route.started && !route.completed) { // Concept
                    return 1
                }

                return 2 // Afgerond
            }

            const isUnderway = (route) => route.started && !route.completed

            const startedRoutes = routes.filter(isUnderway).sort((a, b) => (getETADeviation(b) || 0) - (getETADeviation(a) || 0))
            const otherRoutes = routes.filter((route) => !isUnderway(route)).sort((a, b) => otherStatusOrder(a) - otherStatusOrder(b))

            if (sortOrder === 'desc') {
                startedRoutes.reverse()
            }

            return [...startedRoutes, ...otherRoutes]
        }

        if (routeSort === 'createdAt') {
            routes = routes.sort((a, b) => {
                const aCreatedAt = new Date(parseInt(a._id.substring(0, 8), 16) * 1000)
                const bCreatedAt = new Date(parseInt(b._id.substring(0, 8), 16) * 1000)
                return bCreatedAt - aCreatedAt
            })
        }

        if (routeSort === 'startTime') {
            routes = routes.sort((a, b) => {
                const getTimeString = (route, isOpen) => {
                    if (isOpen) {
                        if (route.startTime) {
                            return moment(route.startTime).format('HH:mm')
                        }

                        return route.plannedStartTime
                    } else {
                        if (route.startTime) {
                            return moment(route.startTime).format('HH:mm')
                        }
                    }

                    return '00:00'
                }

                const aStartTime = getTimeString(a, activeRouteTab === 'open')
                const bStartTime = getTimeString(b, activeRouteTab === 'open')

                return aStartTime.localeCompare(bStartTime)
            })
        }

        if (routeSort === 'distance') {
            routes = routes.sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0))
        }

        if (routeSort === 'duration') {
            routes = routes.sort((a, b) => ((activeRouteTab === 'open' ? a.plannedDuration : a.duration) ?? 0) - ((activeRouteTab === 'open' ? b.plannedDuration : b.duration) ?? 0))
        }

        if (routeSort === 'differenceDuration') {
            routes = routes.sort((a, b) => {
                const delayA = a.duration - a.plannedDuration
                const delayB = b.duration - b.plannedDuration

                return delayA - delayB
            })
        }

        if (routeSort === 'differenceStartTime') {
            routes = routes.sort((a, b) => {
                const aDifferenceStartTime = moment(a.startTime).diff(moment(`${a.date} ${a.plannedStartTime}`), 'seconds') || 0
                const bDifferenceStartTime = moment(b.startTime).diff(moment(`${b.date} ${b.plannedStartTime}`), 'seconds') || 0

                return aDifferenceStartTime - bDifferenceStartTime
            })
        }

        if (routeSort === 'success') {
            routes = routes.sort((a, b) => (a.success || 0) - (b.success || 0))
        }

        if (routeSort === 'notCompleted') {
            routes = routes.sort((a, b) => (a.notCompleted || 0) - (b.notCompleted || 0))
        }

        if (routeSort === 'tooLate') {
            routes = routes.sort((a, b) => (a.tooLate || 0) - (b.tooLate || 0))
        }

        if (routeSort === 'colli') {
            routes = routes.sort((a, b) => {
                const uniqueColliA = a.colli?.filter((c, index, self) => c.barcode ? index === self.findIndex((collo) => collo.barcode === c.barcode) : true)
                const uniqueColliB = b.colli?.filter((c, index, self) => c.barcode ? index === self.findIndex((collo) => collo.barcode === c.barcode) : true)

                const amountOfColliA = uniqueColliA?.length || 0
                const amountOfColliB = uniqueColliB?.length || 0

                return amountOfColliA - amountOfColliB
            })
        }

        if (routeSort === 'stops') {
            routes = routes.sort((a, b) => stopBundles.countStops(a.addresses || []) - stopBundles.countStops(b.addresses || []))
        }

        if (routeSort === 'estimatedEndTime') {
            routes = routes.sort((a, b) => {
                const aEstimatedEndTime = a.estimatedEndTime || '00:00'
                const bEstimatedEndTime = b.estimatedEndTime || '00:00'

                return aEstimatedEndTime.localeCompare(bEstimatedEndTime)
            })
        }

        if (routeSort === 'endTime') {
            routes = routes.sort((a, b) => {
                const aEndTime = a.endTime || '00:00'
                const bEndTime = b.endTime || '00:00'

                return aEndTime.localeCompare(bEndTime)
            })
        }

        if (routeSort === 'vehicle') {
            routes = routes.sort((a, b) => {
                const vehicleA = vehicles.find((vehicle) => vehicle._id === a.vehicleId)?.name || ''
                const vehicleB = vehicles.find((vehicle) => vehicle._id === b.vehicleId)?.name || ''
                return vehicleA ? !vehicleB ? -1 : vehicleA.localeCompare(vehicleB) : 1
            })
        }

        if (routeSort === 'vehicleType') {
            routes = routes.sort((a, b) => {
                const vehicleTypeA = reseller.settings.vehicleTypes.find((vehicleType) => vehicleType._id === a.vehicleTypeId)?.name || ''
                const vehicleTypeB = reseller.settings.vehicleTypes.find((vehicleType) => vehicleType._id === b.vehicleTypeId)?.name || ''

                return vehicleTypeA.localeCompare(vehicleTypeB)
            })
        }

        if (sortOrder === 'desc') {
            routes.reverse()
        }

        return routes
    }

    routeTimeRangeToMs(route) {
        const startTime = route.startTime || (route.plannedStartTime ? `${route.date} ${route.plannedStartTime}` : undefined)
        const endTime = route.endTime || route.estimatedEndTime

        const startMs = moment(startTime).valueOf()
        const endMs = moment(endTime).valueOf()

        if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
            return null
        }

        return {startMs, endMs}
    }

    doesRouteTimeOverlapMs(startMs, endMs, otherStartMs, otherEndMs) {
        return startMs < otherEndMs && otherStartMs < endMs
    }

    getRouteOverlapData(routes, users) {
        if (this.routeOverlapCacheOptimized.routes === routes && this.routeOverlapCacheOptimized.users === users) {
            return this.routeOverlapCacheOptimized.data
        }

        const routesList = Object.values(routes || {})
        const openRoutes = routesList.filter((route) => !route.completed)
        const userIds = (users || []).reduce((result, user) => {
            result[user._id] = true
            return result
        }, {})

        const openRoutesWithRanges = openRoutes.reduce((result, route) => {
            const range = this.routeTimeRangeToMs(route)

            if (range) {
                result.push({
                    route,
                    startMs: range.startMs,
                    endMs: range.endMs
                })
            }

            return result
        }, [])
        const overlapData = {}

        openRoutes.forEach((route) => {
            overlapData[route._id] = {
                overlappingVehicleIds: {},
                overlappingMessengerIds: {},
                routeOverlaps: false,
                messengerOverlaps: false
            }
        })

        for (let i = 0; i < openRoutesWithRanges.length; i++) {
            const current = openRoutesWithRanges[i]
            const currentRoute = current.route
            const currentRouteData = overlapData[currentRoute._id]

            for (let j = i + 1; j < openRoutesWithRanges.length; j++) {
                const other = openRoutesWithRanges[j]
                const otherRoute = other.route
                const otherRouteData = overlapData[otherRoute._id]

                if (!this.doesRouteTimeOverlapMs(current.startMs, current.endMs, other.startMs, other.endMs)) {
                    continue
                }

                if (otherRoute.vehicleId) {
                    currentRouteData.overlappingVehicleIds[otherRoute.vehicleId] = true
                }

                if (currentRoute.vehicleId) {
                    otherRouteData.overlappingVehicleIds[currentRoute.vehicleId] = true
                }

                if (otherRoute.messengerId) {
                    currentRouteData.overlappingMessengerIds[otherRoute.messengerId] = true
                }

                if (currentRoute.messengerId) {
                    otherRouteData.overlappingMessengerIds[currentRoute.messengerId] = true
                }
            }
        }

        openRoutes.forEach((route) => {
            const routeData = overlapData[route._id]
            routeData.routeOverlaps = !!(route.vehicleId && routeData.overlappingVehicleIds[route.vehicleId])
            routeData.messengerOverlaps = !!(route.messengerId && userIds[route.messengerId] && routeData.overlappingMessengerIds[route.messengerId])
        })

        this.routeOverlapCacheOptimized = {
            routes,
            users,
            data: overlapData
        }

        return overlapData
    }

    getRouteOverlapDataLegacy(routes, users) {
        if (this.routeOverlapCacheLegacy.routes === routes && this.routeOverlapCacheLegacy.users === users) {
            return this.routeOverlapCacheLegacy.data
        }

        const openRoutes = Object.values(routes || {}).filter((route) => !route.completed)
        const userIds = (users || []).reduce((result, user) => {
            result[user._id] = true
            return result
        }, {})

        const overlapData = {}

        openRoutes.forEach((route) => {
            overlapData[route._id] = {
                overlappingVehicleIds: {},
                overlappingMessengerIds: {},
                routeOverlaps: false,
                messengerOverlaps: false
            }
        })

        openRoutes.forEach((route) => {
            const routeData = overlapData[route._id]
            const thisRouteStart = route.startTime || (route.plannedStartTime ? `${route.date} ${route.plannedStartTime}` : undefined)
            const thisRouteEnd = route.endTime || route.estimatedEndTime

            openRoutes.forEach((otherRoute) => {
                if (otherRoute._id === route._id) {
                    return
                }

                const startTime = otherRoute.startTime || (otherRoute.plannedStartTime ? `${otherRoute.date} ${otherRoute.plannedStartTime}` : undefined)
                const endTime = otherRoute.endTime || otherRoute.estimatedEndTime

                const doesOverlap = moment(thisRouteStart).isBetween(moment(startTime), moment(endTime), 'minute', '[)') || moment(thisRouteEnd).isBetween(moment(startTime), moment(endTime), 'minute', '(]')

                if (!doesOverlap) {
                    return
                }

                if (otherRoute.vehicleId) {
                    routeData.overlappingVehicleIds[otherRoute.vehicleId] = true
                }

                if (otherRoute.messengerId) {
                    routeData.overlappingMessengerIds[otherRoute.messengerId] = true
                }
            })

            routeData.routeOverlaps = !!(route.vehicleId && routeData.overlappingVehicleIds[route.vehicleId])
            routeData.messengerOverlaps = !!(route.messengerId && userIds[route.messengerId] && routeData.overlappingMessengerIds[route.messengerId])
        })

        this.routeOverlapCacheLegacy = {
            routes,
            users,
            data: overlapData
        }

        return overlapData
    }

    onEditRoute(route) {
        this.editRouteModal.open(route)
    }

    getBulkRouteSelectableRoutes() {
        const {sortedRoutes, orderStartDate, bulkRouteAction} = this.state

        return sortedRoutes.filter((route) => route.date === orderStartDate && !getRouteBulkActionDisabledReason(bulkRouteAction, route))
    }

    onSelectBulkRouteAction(bulkRouteAction) {
        this.setState({bulkRouteAction, bulkRouteSelectedIds: {}, bulkRouteAnchorId: ''})
    }

    onCloseBulkRouteAction() {
        this.setState({bulkRouteAction: '', bulkRouteSelectedIds: {}, bulkRouteAnchorId: ''})
    }

    onToggleBulkRouteSelect(routeId, event) {
        const selectableRouteIds = this.getBulkRouteSelectableRoutes().map((route) => route._id)

        // Every click on a route checkbox makes that route the start of the next shift-click range
        this.setState(({bulkRouteSelectedIds, bulkRouteAnchorId}) => ({
            bulkRouteSelectedIds: toggleRouteBulkSelection(bulkRouteSelectedIds, selectableRouteIds, routeId, !!event?.shiftKey, bulkRouteAnchorId),
            bulkRouteAnchorId: routeId
        }))
    }

    onToggleBulkRouteSelectAll() {
        const {bulkRouteSelectedIds} = this.state
        const selectableRoutes = this.getBulkRouteSelectableRoutes()
        const allSelected = selectableRoutes.every((route) => bulkRouteSelectedIds[route._id])

        this.setState({bulkRouteSelectedIds: allSelected ? {} : _.fromPairs(selectableRoutes.map((route) => [route._id, true]))})
    }

    async onClickRunBulkRouteAction() {
        const {bulkRouteAction, bulkRouteSelectedIds, routes, users, useOptimizedPlannerRendering} = this.state
        const {reseller} = this.props

        const selectedRoutes = this.getBulkRouteSelectableRoutes().filter((route) => bulkRouteSelectedIds[route._id])
        let routeOverlapDataById

        const done = await runRouteBulkAction(bulkRouteAction, selectedRoutes, {
            popup: this.popup,
            getRoute: (routeId) => this.state.routes[routeId],
            getReadyWarnings: (route) => {
                // Computed here instead of in RouteListItem, because the virtualized list doesn't mount every route
                routeOverlapDataById = routeOverlapDataById || (useOptimizedPlannerRendering ? this.getRouteOverlapData(routes, users) : this.getRouteOverlapDataLegacy(routes, users))

                return getRouteReadyWarnings(route, reseller, routeOverlapDataById[route._id])
            },
            onStart: () => this.setState({bulkRouteRunning: true})
        })

        if (done) {
            this.setState({bulkRouteRunning: false, bulkRouteAction: '', bulkRouteSelectedIds: {}, bulkRouteAnchorId: ''})
        }
    }

    async onClickMarker(ids) {
        const {viewOrder, reseller} = this.props
        const {orders} = this.state

        if (ids.length === 1) {
            viewOrder(ids[0])
        } else {
            const options = []

            for (const id of _.uniq(ids)) {
                let order = orders[id]

                if (!order) {
                    order = await new Promise((resolve, reject) => {
                        OrderActions.getOne(id, (err, order) => {
                            if (err) return reject(err)
                            resolve(order)
                        })
                    })
                }

                if (order) {
                    options.push(
                        <OrderCard
                            key={`OrderCard${id}`}
                            order={order}
                            onClick={() => {
                                this.popup.close()
                                viewOrder(id)
                            }}
                            reseller={reseller}
                        />
                    )
                }
            }

            this.popup.openOptions('Rit keuze', 'Kies hieronder de rit die je wilt openen:', options)
        }
    }

    render() {
        const {users, usersObject, orderStartDate, ordersLoading, filteredOrders, selectedOrders, routes, activeRouteTab, selectedRouteId, someRouteHidden, focusedRouteId, mapOptions, showRoutePanel, showOrderPanel, showOrdersOnMap, showCompletedAddressesOnMap, showRoutesOnMap, showStopNumbersOnMap, centerViewOnSelected, routeSort, sortedRoutes, sortOrder, useOptimizedPlannerRendering, bulkRouteAction, bulkRouteSelectedIds, bulkRouteRunning, mapOptions: {expandRouteListItems}} = this.state
        const {reseller, user, viewOrder, selectedBranchIds} = this.props

        const routeOverlapDataById = useOptimizedPlannerRendering ? this.getRouteOverlapData(routes, users) : {}

        const visibleSortedRoutes = sortedRoutes.filter((route) => {
            return route.date === orderStartDate && ((activeRouteTab === 'open' && !route.completed) || (activeRouteTab === 'completed' && route.completed))
        })

        const notCompletedOrders = Object.values(filteredOrders).filter((order) => order.status === dbCodes.status.nietAfgerond())
        const nonCancelledOrders = Object.values(filteredOrders).filter((order) => order.status !== dbCodes.status.afgerond() && order.status !== dbCodes.status.nietAfgerond() && order.status !== dbCodes.status.geannuleerd())

        const bulkRouteSelectableRoutes = bulkRouteAction ? this.getBulkRouteSelectableRoutes() : []
        const nrOfBulkRoutesSelected = bulkRouteSelectableRoutes.filter((route) => bulkRouteSelectedIds[route._id]).length
        const listBulkRouteAction = activeRouteTab === 'open' ? bulkRouteAction : ''

        // TODO remove this from render to improve performance
        let nrOfOrders = 0
        let nrOfAddresses = 0
        let nrOfAddressesWithoutPosition = 0
        let nrOfColli = 0

        const {nrOfRoutes, nrOfStops: nrOfAddressesInRoute, nrOfColli: nrOfColliInRoute, totalDistance, totalDuration, lastEndTime, totalSuccess, totalNotCompleted, totalTooLate, totalDelay} = getRoutePanelTotals(visibleSortedRoutes, activeRouteTab)

        nonCancelledOrders.map((order) => {
            if (!order.routeId) {
                nrOfOrders += 1
                nrOfAddresses += order.addresses.length
                nrOfColli += order.colli.length

                order.addresses.map((address) => {
                    if (!address.position) {
                        nrOfAddressesWithoutPosition += 1
                    }
                })
            }
        })

        nrOfOrders += notCompletedOrders.length

        return (
            <Row style={{flexShrink: 0, flex: 1}}>
                <Row style={{flexShrink: 0, alignItems: 'center', width: 'fit-content'}}>
                    <Row style={{flexShrink: 0,
                        height: '100%',
                        width: showRoutePanel && routes[selectedRouteId] ? 850 : showRoutePanel ? 400 : 0,
                        overflow: 'hidden', // Hide route panel when closed
                        transition: 'all 0.2s ease',
                        borderRight: `1px solid ${Colors.grey40}`
                    }}
                    >
                        <Stack style={{width: 400, flexShrink: 0, alignItems: 'center'}}>
                            <Row style={{flexShrink: 0, alignItems: 'center'}}>
                                <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, minWidth: 'auto'}} />

                                <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, minWidth: 'auto'}}>
                                    <H6 style={{marginBottom: 12, marginTop: 12}}>Routes</H6>
                                </Row>

                                <IconButton
                                    icon='mdi mdi-close'
                                    onClick={OrderActions.setShowRoutePanel}
                                />
                            </Row>

                            <Tabs
                                style={{width: '100%', marginBottom: 0, paddingBottom: 2}}
                                value={activeRouteTab}
                                onChange={(event) => OrderActions.setActiveRouteTab(event.target.value)}
                            >
                                <Tab style={{width: '100%'}} value='open'>Open</Tab>
                                <Tab style={{width: '100%'}} value='completed'>Afgerond</Tab>
                            </Tabs>

                            <Row style={{marginTop: 12, columnGap: formSpacing.columnGap, paddingLeft: 6, paddingRight: 6, width: '100%', alignItems: 'center', minWidth: 'auto'}}>
                                <IconButton
                                    icon={`mdi mdi-sort-${sortOrder === 'asc' ? 'ascending' : 'descending'}`}
                                    onClick={() => this.setState({sortOrder: sortOrder === 'asc' ? 'desc' : 'asc'})}
                                />

                                <Select
                                    label={t('sortBy')}
                                    options={activeRouteTab === 'open' ?
                                        [
                                            {
                                                title: t('sortRouteName'),
                                                value: 'name'
                                            },
                                            {
                                                title: t('status'),
                                                value: 'status'
                                            },
                                            {
                                                title: t('sortProgress'),
                                                value: 'progress'
                                            },
                                            {
                                                title: t('startTime'),
                                                value: 'startTime'
                                            },
                                            {
                                                title: t('messenger'),
                                                value: 'messenger'
                                            },
                                            {
                                                title: t('vehicle'),
                                                value: 'vehicle'
                                            },
                                            {
                                                title: t('vehicleKind'),
                                                value: 'vehicleType'
                                            },
                                            {
                                                title: t('nrOfStops'),
                                                value: 'stops'
                                            },
                                            {
                                                title: t('nrOfColli'),
                                                value: 'colli'
                                            },
                                            {
                                                title: t('distance'),
                                                value: 'distance'
                                            },
                                            {
                                                title: t('calculatedDuration'),
                                                value: 'duration'
                                            },
                                            {
                                                title: t('calculatedEndTime'),
                                                value: 'estimatedEndTime'
                                            },
                                            {
                                                title: t('timeCreated'),
                                                value: 'createdAt'
                                            }
                                        ] :
                                        [
                                            {
                                                title: t('sortRouteName'),
                                                value: 'name'
                                            },
                                            {
                                                title: t('startTime'),
                                                value: 'startTime'
                                            },
                                            {
                                                title: t('differenceStartTime'),
                                                value: 'differenceStartTime'
                                            },
                                            {
                                                title: t('messenger'),
                                                value: 'messenger'
                                            },
                                            {
                                                title: t('vehicle'),
                                                value: 'vehicle'
                                            },
                                            {
                                                title: t('vehicleKind'),
                                                value: 'vehicleType'
                                            },
                                            {
                                                title: t('nrOfCompleted'),
                                                value: 'success'
                                            },
                                            {
                                                title: t('nrOfNotCompleted'),
                                                value: 'notCompleted'
                                            },
                                            {
                                                title: t('nrOfTooLate'),
                                                value: 'tooLate'
                                            },
                                            {
                                                title: t('completedDuration'),
                                                value: 'duration'
                                            },
                                            {
                                                title: t('differenceDuration'),
                                                value: 'differenceDuration'
                                            },
                                            {
                                                title: t('endTime'),
                                                value: 'endTime'
                                            }
                                        ]}
                                    value={routeSort}
                                    onChange={(event) => this.setState({routeSort: event.target.value})}
                                />

                                <RadioGroup
                                    style={{flex: 0}}
                                    options={[
                                        {
                                            label: '',
                                            value: 'expanded',
                                            icon: 'mdi mdi-view-list'
                                        },
                                        {
                                            label: '',
                                            value: 'collapsed',
                                            icon: 'mdi mdi-format-list-bulleted'
                                        }
                                    ]}
                                    value={expandRouteListItems ? 'expanded' : 'collapsed'}
                                    onChange={(event) => {
                                        OrderActions.setMapOptions({expandRouteListItems: event.target.value === 'expanded'})
                                    }}
                                />
                            </Row>

                            {activeRouteTab === 'open' &&
                                <Row style={{alignItems: 'center', gap: formSpacing.tight, marginTop: 12, paddingBottom: 6, position: 'relative', width: 388}}>
                                    <Button
                                        style={{width: '100%'}}
                                        variant='outline-white'
                                        icon='mdi mdi-plus'
                                        label={t('newRoute')}
                                        onClick={() => this.editRouteModal.open()}
                                    />

                                    <Popover
                                        placement='left'
                                        popoverStyle={{width: 'fit-content'}}
                                        content={
                                            <div style={{width: 'fit-content'}}>
                                                <S1 style={{padding: '6px 12px'}}>{t('routeActions')}</S1>

                                                {routeBulkActions.map((action) => (
                                                    <ListItem
                                                        key={action}
                                                        style={{color: action === 'remove' ? Colors.errorBright : undefined}}
                                                        size='sm'
                                                        icon={routeBulkActionOptions[action].icon}
                                                        disabled={bulkRouteRunning}
                                                        onClick={() => this.onSelectBulkRouteAction(action)}
                                                    >
                                                        {t(routeBulkActionOptions[action].label)}
                                                    </ListItem>
                                                ))}
                                            </div>
                                        }
                                    >
                                        <IconButton
                                            icon='mdi mdi-dots-vertical'
                                        />
                                    </Popover>
                                </Row>
                            }

                            {activeRouteTab === 'open' && bulkRouteAction &&
                                <Row style={{alignItems: 'center', marginTop: 6, paddingBottom: 6, width: 388}}>
                                    <Check
                                        checked={bulkRouteSelectableRoutes.length > 0 && nrOfBulkRoutesSelected === bulkRouteSelectableRoutes.length}
                                        disabled={!bulkRouteSelectableRoutes.length || bulkRouteRunning}
                                        onChange={() => this.onToggleBulkRouteSelectAll()}
                                    />

                                    <Button
                                        style={{flex: 1}}
                                        label={`${t(routeBulkActionOptions[bulkRouteAction].label)} (${nrOfBulkRoutesSelected})`}
                                        disabled={!nrOfBulkRoutesSelected}
                                        loading={bulkRouteRunning}
                                        onClick={() => this.onClickRunBulkRouteAction()}
                                    />

                                    <IconButton
                                        icon='mdi mdi-close'
                                        tooltip={t('routeBulkCancel')}
                                        disabled={bulkRouteRunning}
                                        onClick={() => this.onCloseBulkRouteAction()}
                                    />
                                </Row>
                            }

                            {useOptimizedPlannerRendering ?
                                <RouteList
                                    routes={visibleSortedRoutes}
                                    users={users}
                                    focusedRouteId={focusedRouteId}
                                    selectedRouteId={selectedRouteId}
                                    reseller={reseller}
                                    routeOverlapDataById={routeOverlapDataById}
                                    view={expandRouteListItems ? 'expanded' : 'collapsed'}
                                    onEditRoute={this.onEditRoute}
                                    bulkAction={listBulkRouteAction}
                                    bulkSelectedIds={bulkRouteSelectedIds}
                                    bulkRunning={bulkRouteRunning}
                                    onToggleBulkSelect={this.onToggleBulkRouteSelect}
                                /> :
                                <div style={{flex: 1, width: '100%', overflowY: 'auto', padding: 6}}>
                                    {visibleSortedRoutes.map((route) => {
                                        return (
                                            <RouteListItem
                                                key={route._id}
                                                users={users}
                                                route={route}
                                                onEditRoute={this.onEditRoute}
                                                focusedRouteId={focusedRouteId}
                                                selectedRouteId={selectedRouteId}
                                                reseller={reseller}
                                                routes={routes}
                                                view={expandRouteListItems ? 'expanded' : 'collapsed'}
                                                bulkAction={listBulkRouteAction}
                                                bulkSelected={!!bulkRouteSelectedIds[route._id]}
                                                bulkRunning={bulkRouteRunning}
                                                onToggleBulkSelect={this.onToggleBulkRouteSelect}
                                            />
                                        )
                                    })}
                                </div>
                            }

                            <Panel style={{padding: 6, background: Colors.white, borderLeft: 'none', borderRight: 'none'}}>
                                <Row style={{flexShrink: 0, justifyContent: 'center', marginBottom: 6}}>
                                    <S1>{`${nrOfRoutes} ${nrOfRoutes === 1 ? t('route') : t('routes')}`}</S1>
                                </Row>

                                {activeRouteTab === 'open' &&
                                    <Row style={{flexShrink: 0, justifyContent: 'space-between', gap: 12}}>
                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <ToolTip text={t('nrOfStops')} placement='top'>
                                                <Icon style={{marginRight: 0}} icon='mdi mdi-home-outline' />
                                            </ToolTip>
                                            <P>{nrOfAddressesInRoute}</P>
                                        </Stack>

                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <ToolTip text={t('nrOfColli')} placement='top'>
                                                <Icon style={{marginRight: 0}} icon='mdi mdi-package-variant-closed' />
                                            </ToolTip>

                                            <P>{nrOfColliInRoute}</P>
                                        </Stack>

                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <ToolTip text={t('distance')} placement='top'>
                                                <Icon style={{marginRight: 0}} icon='mdi mdi-road-variant' />
                                            </ToolTip>

                                            <P>
                                                {totalDistance < 10000 ?
                                                    `${(totalDistance / 1000).toFixed(1)} km` :
                                                    `${(totalDistance / 1000).toFixed(0)} km`
                                                }

                                            </P>
                                        </Stack>

                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <ToolTip text={t('calculatedDuration')} placement='top'>
                                                <Icon style={{marginRight: 0}} icon='mdi mdi-timer-sand' />
                                            </ToolTip>

                                            <P>
                                                {
                                                    `${parseInt(totalDuration / 3600) < 10 ? '0' : ''}${parseInt(totalDuration / 3600)}:${parseInt((totalDuration % 3600) / 60) < 10 ? '0' : ''}${parseInt((totalDuration % 3600) / 60)}`
                                                }
                                            </P>
                                        </Stack>

                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <Icon style={{marginRight: 0}} icon='mdi mdi-clock-check-outline' />

                                            <P>{lastEndTime ? moment(lastEndTime).format('HH:mm') : ''}</P>
                                        </Stack>
                                    </Row>
                                }

                                {activeRouteTab === 'completed' &&
                                    <Row style={{flexShrink: 0, justifyContent: 'space-between', gap: 12}}>
                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <ToolTip text={t('completed')} placement='top'>
                                                <Icon style={{marginRight: 0}} icon='mdi mdi-check-bold' />
                                            </ToolTip>

                                            <P>{totalSuccess || 0}</P>
                                        </Stack>

                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <ToolTip text={t('notCompleted')} placement='top'>
                                                <Icon style={{marginRight: 0}} icon='mdi mdi-close-thick' />
                                            </ToolTip>

                                            <P>{totalNotCompleted || 0}</P>
                                        </Stack>

                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <ToolTip text={t('tooLate')} placement='top'>
                                                <Icon style={{marginRight: 0}} icon='mdi mdi-clock-remove-outline' />
                                            </ToolTip>

                                            <P>{totalTooLate || 0}</P>

                                        </Stack>

                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <ToolTip text={t('calculatedDuration')} placement='top'>
                                                <Icon
                                                    style={{marginRight: 0}}
                                                    icon='mdi mdi-timer-sand-complete'
                                                />
                                            </ToolTip>

                                            <P>{formatDuration(totalDuration)}</P>
                                        </Stack>

                                        <Stack style={{flex: 1, alignItems: 'center'}}>
                                            <ToolTip text={t('etaDeviation')} placement='top'>
                                                <Icon style={{marginRight: 0}} icon='mdi mdi-flag-checkered' />
                                            </ToolTip>

                                            <P>{formatDuration(totalDelay, true)}</P>
                                        </Stack>
                                    </Row>
                                }
                            </Panel>
                        </Stack>

                        <RoutePanel
                            usersObject={usersObject}
                            route={routes[selectedRouteId]}
                            focusedRouteId={focusedRouteId}
                            viewOrder={viewOrder}
                            onEditRoute={(route) => this.editRouteModal.open(route)}
                            onClose={() => {
                                if (focusedRouteId) {
                                    OrderActions.setRouteFocus('')
                                }

                                OrderActions.setSelectedRoute('')
                            }}
                            ref={(ref) => this.routePanel = ref}
                            reseller={reseller}
                            routes={routes}
                            orders={filteredOrders}
                        />
                    </Row>
                </Row>

                <Stack style={{flex: 1}}>
                    {/* The buttons that open the panels are centred on the map, so they stay in view however high the Bezetting panel is */}
                    <div style={{position: 'relative', flex: 1, minHeight: 0}}>
                        <PlanningMap
                            reseller={reseller}
                            user={user}
                            selectedBranchIds={selectedBranchIds}
                            loading={ordersLoading}
                            date={orderStartDate}
                            orders={filteredOrders}
                            selectedOrders={selectedOrders}
                            options={mapOptions}
                            showOrdersOnMap={showOrdersOnMap}
                            showCompletedAddressesOnMap={showCompletedAddressesOnMap}
                            showRoutesOnMap={showRoutesOnMap}
                            showStopNumbersOnMap={showStopNumbersOnMap}
                            focusedRouteId={focusedRouteId}
                            someRouteHidden={someRouteHidden}
                            activeRouteTab={activeRouteTab}
                            routes={routes}
                            onClick={this.onClickMarker.bind(this)}
                            onChange={OrderActions.setSelected}
                            centerViewOnSelected={centerViewOnSelected}
                        />

                        {!showRoutePanel &&
                            <Button
                                variant='outline-white'
                                style={{...panelButtonStyle, left: 0}}
                                buttonStyle={{paddingLeft: 3, paddingRight: 3, borderRadius: getSplitRadius('right')}}
                                onClick={OrderActions.setShowRoutePanel}
                            >
                                <i className='mdi mdi-chevron-right' />
                            </Button>
                        }

                        {!showOrderPanel &&
                            <Button
                                variant='outline-white'
                                style={{...panelButtonStyle, right: 0}}
                                buttonStyle={{paddingLeft: 3, paddingRight: 3, borderRadius: getSplitRadius('left')}}
                                onClick={OrderActions.setShowOrderPanel}
                            >
                                <i className='mdi mdi-chevron-left' />
                            </Button>
                        }
                    </div>

                    <VehiclePanel
                        reseller={reseller}
                        user={user}
                        routes={_.pickBy(routes, (route) => route.date === orderStartDate)}
                        date={orderStartDate}
                    />
                </Stack>

                <Row style={{flexShrink: 0, alignItems: 'center', width: 'fit-content'}}>
                    <Stack style={{
                        flex: 1,
                        height: '100%',
                        width: showOrderPanel ? 350 : 0,
                        overflow: 'hidden', // Hide order panel when closed
                        transition: 'all 0.2s ease',
                        borderLeft: `1px solid ${Colors.grey40}`
                    }}
                    >
                        <Stack style={{flex: 1, width: 350, flexShrink: 0, alignItems: 'center'}}>
                            <Row style={{flexShrink: 0, alignItems: 'center'}}>
                                <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, minWidth: 'auto'}} />

                                <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, minWidth: 'auto'}}>
                                    <H6 style={{marginBottom: 12, marginTop: 12}}>Ritten</H6>
                                </Row>

                                <IconButton
                                    icon='mdi mdi-close'
                                    onClick={OrderActions.setShowOrderPanel}
                                />
                            </Row>

                            <OrderList
                                date={orderStartDate}
                                filteredOrders={filteredOrders}
                                viewOrder={viewOrder}
                                reseller={reseller}
                            />

                            <Panel style={{padding: 6, background: Colors.white, borderLeft: 'none', borderRight: 'none'}}>
                                <Row style={{flexShrink: 0, justifyContent: 'center', marginBottom: 6}}>
                                    <S1>{`${nrOfOrders} ${nrOfOrders === 1 ? 'Rit' : 'Ritten'}`}</S1>
                                </Row>

                                <Row style={{flexShrink: 0, justifyContent: 'space-between', gap: 12}}>
                                    <Stack style={{flex: 1, alignItems: 'center'}}>
                                        <ToolTip text='Niet afgerond' placement='top'>
                                            <Icon icon='mdi mdi-circle' style={{color: Colors.warningBright, marginLeft: -2, marginRight: 0}} />
                                        </ToolTip>

                                        <P>{notCompletedOrders.length}</P>
                                    </Stack>

                                    <Stack style={{flex: 1, alignItems: 'center'}}>
                                        <ToolTip text='Foutieve adressen' placement='top'>
                                            <Icon style={{marginRight: 0}} icon='mdi mdi-alert' />
                                        </ToolTip>

                                        <P>{nrOfAddressesWithoutPosition}</P>
                                    </Stack>

                                    <Stack style={{flex: 1, alignItems: 'center'}}>
                                        <ToolTip text='Aantal adressen' placement='top'>
                                            <Icon style={{marginRight: 0}} icon='mdi mdi-home-outline' />
                                        </ToolTip>
                                        <P>{nrOfAddresses}</P>
                                    </Stack>

                                    <Stack style={{flex: 1, alignItems: 'center'}}>
                                        <ToolTip text='Aantal colli' placement='top'>
                                            <Icon style={{marginRight: 0}} icon='mdi mdi-package-variant-closed' />
                                        </ToolTip>
                                        <P>{nrOfColli}</P>
                                    </Stack>

                                    <Stack style={{flex: 1, alignItems: 'center'}}>
                                        <ToolTip text='Aantal geselecteerd' placement='top'>
                                            <Icon style={{marginRight: 0}} icon='mdi mdi-select' />
                                        </ToolTip>

                                        <P>{selectedOrders.length}</P>
                                    </Stack>

                                </Row>
                            </Panel>
                        </Stack>
                    </Stack>
                </Row>

                <Popup closeButton ref={(ref) => this.popup = ref} />
                <EditRouteModal reseller={reseller} users={usersObject} ref={(ref) => this.editRouteModal = ref} />
            </Row>
        )
    }
}

export default withVehiclesHook(Planning)
